import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, output, signal } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatError, MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatTooltipModule } from '@angular/material/tooltip';
import { L10N_LOCALE, L10nTranslatePipe } from 'angular-l10n';

import { DatePipe } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { RouterLink } from '@angular/router';
import { catchError, defer, of, take, tap } from 'rxjs';
import { FileModelHelper, FileModelView } from 'src/app/files/file-model-helper';
import { AdminsApi } from 'src/app/hickory-api';
import { AccessControl } from 'src/app/shared/authentication/access-control';
import { AuthService } from 'src/app/shared/authentication/auth.service';
import { FilesViewConstants } from '../file-view-context';

@Component({
  selector: 'app-file-description-input',
  imports: [
    DatePipe,
    FormsModule,
    L10nTranslatePipe,
    MatButtonModule,
    MatCardModule,
    MatError,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatTooltipModule,
    ReactiveFormsModule,
    RouterLink
  ],
  standalone: true,
  templateUrl: './file-description-input.component.html',
  styleUrl: './file-description-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FileDescriptionInputComponent implements OnInit {
  private readonly auth = inject(AuthService);
  private readonly adminApi = inject(AdminsApi);
  public readonly locale = inject(L10N_LOCALE);
  public readonly showSaveButton = input<boolean>(false);
  public readonly showUseButton = input<boolean>(false);
  public readonly showDeleteButton = input<boolean>(false);
  public readonly descriptionOverride = input<string>(null);
  public readonly fileDescription = input.required<FormControl>();
  public readonly fileResize = input<FormControl>(null);
  public readonly fileViewModel? = input<FileModelView>();
  public readonly miniImage = input<boolean>(false);
  public readonly saveFile = output<void>();
  public readonly fileSelected = output<void>();
  public readonly deleteFile = output<void>();
  public readonly uploadFile = output<void>();
  public readonly uploadCancelled = output<void>();

  protected descriptionInstructions = computed(() => this.descriptionOverride() ?? this.fileViewModel()?.instructions);
  protected isImageFile = computed(() => this.fileViewModel()?.isImageFile);
  protected isDisabled = () => this.isImageFile() && (this.fileDescription().invalid || !this.fileDescription().value);
  protected getIcon = computed(() => FileModelHelper.getIcon(this.fileViewModel()));
  protected getDate = computed(() => FileModelHelper.getDate(this.fileViewModel()));
  protected contentType = computed(() => FileModelHelper.getContentType(this.fileViewModel()));

  protected get canViewAdmins() {
    return this.auth.isSuperAdmin() || this.auth.hasPermission(AccessControl.AdminManage);
  }

  protected get actions() {
    if (this.miniImage()) {
      return null;
    }

    if (this.fileViewModel()?.isNew) {
      return [
        {
          ...FilesViewConstants.UPLOAD_BUTTON,
          emit: () => this.uploadFile.emit(),
          disabled: this.isDisabled()
        },
        {
          ...FilesViewConstants.CANCEL_UPLOAD_BUTTON,
          icon: 'check_circle',
          emit: () => this.uploadCancelled.emit()
        }
      ];
    } else {
      return this.getActionForExistingImage();
    }
  }

  protected uploadedBy = signal<string>(null);
  public ngOnInit(): void {
    if (this.fileViewModel()?.OwnerId && this.canViewAdmins) {
      defer(() => this.adminApi.getSingleAdminProfile(this.fileViewModel().OwnerId)
        .pipe(
          take(1),
          tap(admin => {
            if (admin?.item && !admin.hasErrors && admin.isValid) {
              this.uploadedBy.set(admin.item.username);
            } else {
              this.uploadedBy.set(this.fileViewModel()?.OwnerId.toString());
            }
          }),
          catchError(() => {
            this.uploadedBy.set(this.fileViewModel()?.OwnerId.toString());
            return of(null);
          }),
        )).subscribe();
    }
  }

  protected getActionForExistingImage() {
    const actions: { label: string, tip: string, icon: string, color: string, disabled?: boolean, emit: () => void }[] = [];
    if (this.showSaveButton()) {
      actions.push({
        ...FilesViewConstants.SAVE_BUTTON,
        emit: () => this.saveFile.emit(),
        disabled: (
          this.isDisabled() || this.fileViewModel()?.Description === this.fileDescription().value
        )
      });
    }
    if (this.showUseButton()) {
      actions.push({
        ...FilesViewConstants.SELECT_BUTTON,
        emit: () => this.fileSelected.emit(),
        disabled: this.isDisabled()
      });
    }
    if (this.showDeleteButton()) {
      actions.push({
        ...FilesViewConstants.DELETE_BUTTON,
        emit: () => this.deleteFile.emit()
      });
    }
    return actions;
  }
}
